import { Directive, EventEmitter, inject, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';
import {
  getActiveDamageTickets,
  getActiveDamageTicketsActionState,
  getActiveRequestTickets,
  getActiveRequestTicketsActionState,
} from '../../../+state';
import { ITicketResidentOverview, ITicketResidentOverviewEdge, LoadingType } from '../../../models';
import { WidgetBaseComponent } from '../widget.base-component';

export interface ITicketData extends ITicketResidentOverview {
  category: {
    id: string;
    name: string;
    primary?: string;
    secondary?: string;
  };
}

@Directive()
export class TicketsWidgetBaseComponent extends WidgetBaseComponent {
  @Output() tapped = new EventEmitter();
  store = inject(Store);
  damageTickets$ = this.store.select(getActiveDamageTickets);
  damageTicketsState$ = this.store.select(getActiveDamageTicketsActionState);
  requestTickets$ = this.store.select(getActiveRequestTickets);
  requestTicketsState$ = this.store.select(getActiveRequestTicketsActionState);
  loadingTickets$: Observable<boolean> = combineLatest([
    this.damageTicketsState$,
    this.requestTicketsState$,
  ]).pipe(map(([damageState, requestState]) => damageState.pending || requestState.pending));
  combinedTickets$ = combineLatest([this.damageTickets$, this.requestTickets$]).pipe(
    map(([damageTickets, requestTickets]) => this.combineTickets(damageTickets, requestTickets)),
    map(combinedTickets => {
      return combinedTickets.sort((a, b) => {
        const datumA = new Date(a.node.updated as string).getTime();
        const datumB = new Date(b.node.updated as string).getTime();
        return datumB - datumA;
      });
    })
  );
  isMenuVisible = false;
  public loadingTypes: LoadingType[] = [];

  private combineTickets(
    damageTickets: ITicketResidentOverviewEdge[],
    requestTickets: ITicketResidentOverviewEdge[]
  ): ITicketResidentOverviewEdge[] {
    const result: ITicketResidentOverviewEdge[] = [];
    const desiredNumberOfTickets = 4;

    const damageSelection = damageTickets.slice(0, 2);
    result.push(...damageSelection);

    const requestSelection = requestTickets.slice(0, 2);
    result.push(...requestSelection);

    if (result.length < desiredNumberOfTickets) {
      const remainingDamage = damageTickets.slice(2);
      const needed = desiredNumberOfTickets - result.length;
      result.push(...remainingDamage.slice(0, needed));
    }

    if (result.length < desiredNumberOfTickets) {
      const remainingRequests = requestTickets.slice(2);
      const needed = desiredNumberOfTickets - result.length;
      result.push(...remainingRequests.slice(0, needed));
    }

    return result.slice(0, desiredNumberOfTickets);
  }
}
